import React, { useEffect, useState } from "react";
import ConsumerForm from "./ConsumerForm";
import { getConsumers, deleteConsumer, openConsumerPdf } from "../api/consumerService";
import { toast } from "react-toastify";

const ConsumerList = () => {
    const [consumers, setConsumers] = useState([]);
    const [editingConsumer, setEditingConsumer] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loadingPdfId, setLoadingPdfId] = useState(null);

    const itemsPerPage = 30;

    const docTitles = {
        WCR: "Work Completion Report",
        ANP: "Annexure and Proforma",
        NA: "Net Agreement Bond",
        MA: "Model Agreement",
        DC: "Declaration",
        RO: "Release Order",
    };

    // 🔥 SERVER-SIDE PAGINATION FETCH
    const fetchConsumers = async (page = 1) => {
        try {
            const result = await getConsumers(page, itemsPerPage);

            setConsumers(result.data || []);
            setCurrentPage(result.currentPage || 1);
            setTotalPages(result.totalPages || 1);
        } catch (error) {
            console.error("Error fetching consumers:", error);
            setConsumers([]);
        }
    };

    useEffect(() => {
        fetchConsumers(currentPage);
    }, [currentPage]);
    const handleEdit = (consumer) => {
        setEditingConsumer(consumer);
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this record?")) return;
        try {
            await deleteConsumer(id);
            toast.success("Record deleted successfully");
            fetchConsumers(currentPage);
        } catch {
            toast.error("Failed to delete record");
        }
    };

    const openPdf = async (id, type) => {
        try {
            setLoadingPdfId(`${id}-${type}`);
            await openConsumerPdf(id, type);
        } catch {
            toast.error("Failed to open PDF");
        } finally {
            setLoadingPdfId(null);
        }
    };

    // 🔍 SEARCH (current page only)
    const filteredConsumers = consumers.filter((consumer) => {
        const q = searchQuery.toLowerCase();
        return [
            consumer.name,
            consumer.consumerNumber,
            consumer.sanctionNumber,
            consumer.email,
            consumer.mobile,
            consumer.division,
        ].some(v => (v || "").toLowerCase().includes(q));
    });

    return (
        <div className="p-1 max-w-8xl mx-auto">
            {/* HEADER */}
            <div className="flex justify-between items-center mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 tracking-tight">
                    {showForm ? "➕ Add Consumer" : "📋 Consumers List"}
                </h2>

                {!showForm && (
                    <button
                        onClick={() => setShowForm(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg shadow"
                    >
                        Add Consumer
                    </button>
                )}
            </div>

            {!showForm && (
                <>
                    {/* SEARCH */}
                    <div className="mb-4">
                        <input
                            type="text"
                            placeholder="Search consumers..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                        />
                    </div>

                    {/* TABLE */}
                    <div className="overflow-x-auto bg-white shadow rounded-lg">
                        <table className="min-w-full text-sm text-gray-700">
                            <thead className="bg-blue-600 text-white">
                                <tr>
                                    {[
                                        "Name",
                                        "Consumer Number",
                                        "Email",
                                        "Mobile",
                                        "Sanction Number",
                                        "Division",
                                        ...Object.keys(docTitles),
                                        "Actions",
                                    ].map((title, i) => (
                                        <th
                                            key={i}
                                            className="px-4 py-3 text-left font-semibold"
                                        >
                                            {title}
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody className="divide-y divide-gray-200">
                                {filteredConsumers.length > 0 ? (
                                    filteredConsumers.map((consumer) => (
                                        <tr
                                            key={consumer.id}
                                            className="hover:bg-gray-50 transition"
                                        >
                                            <td className="px-4 py-2">{consumer.name}</td>
                                            <td className="px-4 py-2">{consumer.consumerNumber}</td>
                                            <td className="px-4 py-2">{consumer.email}</td>
                                            <td className="px-4 py-2">{consumer.mobile}</td>
                                            <td className="px-4 py-2">{consumer.sanctionNumber}</td>
                                            <td className="px-4 py-2">{consumer.division}</td>

                                            {Object.keys(docTitles).map((doc) => {
                                                const isLoading = loadingPdfId === `${consumer.id}-${doc}`;
                                                return (
                                                    <td key={doc} className="px-2 py-2 text-center">
                                                        {consumer.sanctionCapacity && (
                                                            <button
                                                                onClick={() => openPdf(consumer.id, doc)}
                                                                className="text-blue-600 hover:text-blue-800 disabled:opacity-50"
                                                                title={docTitles[doc]}
                                                                disabled={isLoading}
                                                            >
                                                                {isLoading ? (
                                                                    <span className="animate-spin inline-block w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                                                                ) : (
                                                                    "🖨️"
                                                                )}
                                                            </button>
                                                        )}
                                                    </td>
                                                );
                                            })}

                                            <td className="px-4 py-2">
                                                <div className="flex gap-3 justify-center">
                                                    {/* ✏️ EDIT */}
                                                    <button
                                                        onClick={() => handleEdit(consumer)}
                                                        className="text-blue-600 hover:text-blue-800"
                                                        title="Edit"
                                                    >
                                                        ✏️
                                                    </button>

                                                    {/* 🗑️ DELETE */}
                                                    <button
                                                        onClick={() => handleDelete(consumer.id)}
                                                        className="text-red-600 hover:text-red-800"
                                                        title="Delete"
                                                    >
                                                        🗑️
                                                    </button>
                                                </div>
                                            </td>

                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td
                                            colSpan={12}
                                            className="px-6 py-6 text-center text-gray-500"
                                        >
                                            No consumers found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* PAGINATION */}
                    {totalPages > 1 && (
                        <div className="mt-6 flex justify-center space-x-1">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1 border rounded-l text-sm bg-white hover:bg-gray-100 disabled:opacity-50"
                            >
                                Prev
                            </button>

                            {[...Array(totalPages)].map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentPage(index + 1)}
                                    className={`px-3 py-1 border text-sm ${currentPage === index + 1
                                        ? "bg-blue-600 text-white"
                                        : "bg-white hover:bg-gray-100"
                                        }`}
                                >
                                    {index + 1}
                                </button>
                            ))}

                            <button
                                onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1 border rounded-r text-sm bg-white hover:bg-gray-100 disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* FORM */}
            {showForm && (
                <div className="mt-6 bg-white shadow rounded-lg p-6">
                    <ConsumerForm
                        initialData={editingConsumer}
                        onClose={() => {
                            setShowForm(false);
                            fetchConsumers(currentPage);
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default ConsumerList;
